import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, ExternalLink, Loader2, MapPin } from "lucide-react";
import { type State, stateNames } from "shared";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { useAppForm } from "@/hooks/useCreateForm";

export const Route = createFileRoute("/_authed/_org/tax-setup")({
  component: RouteComponent,
});

function RouteComponent() {
  const { trpc, queryClient } = Route.useRouteContext();
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(trpc.org.getBillingAddress.queryOptions());
  const { mutate, isPending } = useMutation(
    trpc.tax.startTaxCollection.mutationOptions({
      onSuccess: async () => {
        await queryClient.refetchQueries(trpc.auth.session.queryOptions());
        navigate({
          to: "/tax",
        });
      },
    }),
  );

  const stateName = stateNames[data?.state as State];

  const form = useAppForm({
    defaultValues: {
      accountId: "",
      contactUserId: "",
      ntee: undefined as string | undefined,
      state: data?.state === "NC" ? "NC" : ("UT" as const),
    },
    onSubmit: ({ value }) => {
      mutate(value);
    },
  });

  if (data?.state === "UT" || data?.state === "NC") {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Card className="max-w-4xl">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-xl">
                  {data?.state === "UT"
                    ? "Utah Sales Tax Exemption Process"
                    : "North Carolina Sales Tax Collection"}
                </CardTitle>
                <CardDescription>
                  {data?.state === "UT"
                    ? "As a 501(c)(3) organization in Utah, you can claim sales tax exemptions on eligible purchases"
                    : "Set up automatic sales tax collection for your North Carolina organization"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step-by-step process */}
            {data?.state === "UT" ? (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">How It Works</h3>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 font-medium text-blue-600 text-sm">
                      1
                    </div>
                    <div>
                      <p className="font-medium">
                        File Form TC-160 to Get Your Utah Sales Tax Exemption
                        Number
                      </p>
                      <p className="mb-3 text-muted-foreground text-sm">
                        First, you must file form TC-160 with the Utah
                        government to obtain your sales and use account id a
                        501(c)(3) organization. After filing, Utah will mail you
                        copies of the TC-62N form for future reimbursement
                        requests.
                      </p>
                      <div className="flex w-full flex-row justify-around">
                        <a
                          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 font-medium text-sm text-white transition-colors hover:bg-blue-700"
                          href="https://tap.tax.utah.gov/TaxExpress"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          File Online
                          <ExternalLink className="h-4 w-4" />
                        </a>
                        <a
                          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 font-medium text-sm text-white transition-colors hover:bg-blue-700"
                          href="https://tax.utah.gov/forms/current/tc-160.pdf"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          Download TC-160 Form
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 font-medium text-blue-600 text-sm">
                      2
                    </div>
                    <div>
                      <p className="font-medium">
                        We Track Your Sales Tax Automatically
                      </p>
                      <p className="text-muted-foreground text-sm">
                        When you upload receipts, Givefront automatically
                        extracts and tracks sales tax data from your
                        transactions.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 font-medium text-blue-600 text-sm">
                      3
                    </div>
                    <div>
                      <p className="font-medium">
                        File For Reimbursements (TC-62N)
                      </p>
                      <p className="text-muted-foreground text-sm">
                        File form{" "}
                        <a
                          className="underline"
                          href="https://tax.utah.gov/forms/current/tc-62n.pdf"
                          rel="noopener"
                          target="_blank"
                        >
                          TC-62N
                        </a>{" "}
                        once per month to receive reimbursements. Givefront
                        prefills all required information.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">How It Works</h3>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 font-medium text-blue-600 text-sm">
                      1
                    </div>
                    <div>
                      <p className="font-medium">
                        Set Up Your Tax Collection Information
                      </p>
                      <p className="text-muted-foreground text-sm">
                        Provide your organization's tax information and NTEE
                        classification code.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 font-medium text-blue-600 text-sm">
                      2
                    </div>
                    <div>
                      <p className="font-medium">
                        We Track Your Sales Tax Automatically
                      </p>
                      <p className="text-muted-foreground text-sm">
                        When you upload receipts, Givefront automatically
                        extracts and tracks sales tax data from your
                        transactions.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 font-medium text-blue-600 text-sm">
                      3
                    </div>
                    <div>
                      <p className="font-medium">
                        File For Reimbursements (E-585)
                      </p>
                      <p className="text-muted-foreground text-sm">
                        File form{" "}
                        <a
                          href="https://www.ncdor.gov/documents/files/form-e-585-nonprofit-and-governmental-entity-claim-refund-state-county-and-transit-sales-and-use-2/open"
                          rel="noopener"
                          target="_blank"
                          className="underline"
                        >
                          E-585
                        </a>{" "}
                        twice per year to receive reimbursements on sales tax.
                        Givefront tracks and prefills all required information.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <form
              className="space-y-4 border-t pt-4"
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                form.handleSubmit();
              }}
            >
              {data?.state === "UT" && (
                <form.AppField
                  children={(field) => (
                    <field.TextField
                      description="If you have not yet received this, you can leave it blank and add it manually later."
                      descriptionStyle="below"
                      label="Sales and Use Account Id"
                    />
                  )}
                  name="accountId"
                />
              )}
              {data?.state === "NC" && (
                <>
                  <form.AppField
                    children={(field) => (
                      <field.TextField
                        description='Nine digit North Carolina tax account number. Should start with a "72" '
                        descriptionStyle="below"
                        label="NC Tax Account Number"
                      />
                    )}
                    name="accountId"
                  />
                  <form.AppField
                    children={(field) => (
                      <field.TextField
                        description="Your organization's National Taxonomy of Exempt Entities (NTEE) classification code"
                        descriptionStyle="below"
                        label="NTEE Code"
                      />
                    )}
                    name="ntee"
                  />
                </>
              )}
              <form.AppField
                children={(field) => (
                  <field.UserSelectField
                    description={
                      data?.state === "UT"
                        ? "This user will have their contact information prefilled on the TC-62N form."
                        : "This user will be listed as the primary contact for tax-related matters."
                    }
                    descriptionStyle="below"
                    label="Point of contact"
                  />
                )}
                name="contactUserId"
              />
              <Button className="w-full" disabled={isPending} type="submit">
                {isPending && <Loader2 className="animate-spin" />}
                Begin Sales Tax Tracking
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center">
      <Card className="max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
              <MapPin className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-xl">
                Sales Tax Collection Unavailable
              </CardTitle>
              <CardDescription>
                Automatic sales tax collection is not available in {stateName}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            We're working to expand our sales tax automation services to more
            states. Currently, automatic sales tax collection and remittance is
            only available for organizations based in Utah.
          </p>

          <p className="text-muted-foreground text-sm">
            For now, you'll need to handle sales tax obligations manually
            according to {stateName} state requirements.
          </p>

          <div className="border-t pt-4">
            <Link to="/dashboard">
              <Button className="w-full" variant="outline">
                Return to Dashboard
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
